import { Label } from "components/label";
import { CollapsibleSection } from "../../../../../components/collapsibleSection";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { HttpError, useOne, useUpdate } from "@refinedev/core";
import { IBlogIdsItem, IHomepageDetailData, IPostData } from "interfaces";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import Button from "components/button";
import { useNavigate } from "react-router-dom";
import { get } from "lodash";
import toast from "react-hot-toast";
import { FullScreenLoading } from "components/fullScreenLoading";
import { PlusCircleIcon, TrashIcon } from "@heroicons/react/outline";
import Select from "components/select";
import { useState } from "react";
interface IBlogPostField extends IPostData {
  id: string;
}

const EditBlogPostSchema = z.object({
  id: z.string().min(1, "This field is required"),
});

const EditBlogPostsSchema = z.object({
  data: z.array(EditBlogPostSchema).max(9, {
    message: `You can add at most ${9} posts`,
  }),
});

const MAXIMUM_BLOG_POSTS = 3;

const BLOG_BUTTONS = [
  { label: "All", value: "all" },
  { label: "Promo", value: "promo" },
  { label: "Blogs", value: "blogs" },
  { label: "News", value: "news" },
  { label: "Health & Wellness", value: "health" },
  { label: "Others", value: "others" },
];

const BlogForm = ({
  defaultData,
  type,
  blogList,
  isLoadingBlogs,
}: {
  defaultData: any;
  type: string;
  blogList: IBlogIdsItem[];
  isLoadingBlogs: boolean;
}) => {
  const navigate = useNavigate();

  const { mutate, isLoading: isLoading } = useUpdate();

  const onSubmit = (values: { data: IPostData[] }) => {
    const submitValues = values.data.map((value) => `${value.id}`);
    mutate(
      {
        resource: `content/homepage`,
        id: `${defaultData.id}/${type}/blog/update`,
        invalidates: [],
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {
          id: [...submitValues],
        },
      },
      {
        onSuccess: () => {
          toast.success("Update successfully");
          navigate(`/mkt/homepage/detail?default=blog`);
        },
      }
    );
  };

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<{ data: IPostData[] }, HttpError, { data: IPostData[] }>({
    defaultValues: {
      data: defaultData.data,
    },
    resolver: zodResolver(EditBlogPostsSchema),
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "data",
  });

  const formIds = (watch("data") ?? []).map((value) => `${value.id}`);

  const blogs = blogList
    .filter((data: any) => !formIds.includes(data.id))
    .map((blog: IBlogIdsItem) => ({ value: blog.id, label: blog.title_en }));

  return (
    <>
      <form
        className="divide-y divide-gray-100"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="w-full px-4 py-6 sm:px-6">
          <p className="capitalize font-semibold mb-2">{type}</p>
          {fields.length < MAXIMUM_BLOG_POSTS ? (
            <div className="w-full">
              <Button
                type="button"
                className="w-fit p-2.5 text-sm bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100"
                onClick={() => {
                  append({ position: 0, name: "", id: "" });
                }}
              >
                <PlusCircleIcon className="w-6 h-6 mr-1.5" />{" "}
                <span className="shrink-0 text-sm">Add more</span>
              </Button>
            </div>
          ) : (
            <></>
          )}

          <div className="grid gap-x-4 gap-y-6 sm:grid-cols-6 lg:gap-x-6 py-4">
            {fields.map((field: IBlogPostField, index: number) => (
              <div className="sm:col-span-2" key={field.id}>
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <Label
                      id={`blog_${field.id}`}
                      name={`Blog Name ${index + 1}`}
                      required
                    />
                    <button onClick={() => remove(index)}>
                      <TrashIcon className="w-5 h-5 text-brand-500" />
                    </button>
                  </div>
                  <Controller
                    control={control}
                    name={`data.${index}.id`}
                    render={({ field: { onChange, value = "", ref } }) => {
                      return (
                        <Select
                          key={`blog-id__${field.id}`}
                          inputRef={ref}
                          options={blogs}
                          id={`blog-id__${field.id}`}
                          placeholder="Select Organization"
                          defaultValue={
                            blogs.find(
                              (selected: { label: string; value: string }) =>
                                selected.value === value
                            ) ??
                            (field.name && field.id)
                              ? {
                                  label: field.name,
                                  value: field.id,
                                }
                              : ""
                          }
                          value={blogs.find(
                            (selected: { label: string; value: string }) =>
                              selected.value === value
                          )}
                          onChange={(selected: {
                            label: string;
                            value: string;
                          }) => {
                            onChange(selected?.value);
                          }}
                          error={get(errors.data, `${index}.id`, "")}
                          isLoading={isLoadingBlogs}
                        />
                      );
                    }}
                  />
                  {get(errors.data, `${index}.id`, "") ? (
                    <p className="error-text">
                      <span className="font-medium">
                        {get(errors.data, `${index}.id.message`, "")}
                      </span>
                    </p>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="w-full px-4 py-4 sm:px-6 bg-white">
          <div className="flex items-center justify-end gap-x-4">
            <Button className="w-20" type="submit">
              SAVE
            </Button>
          </div>
        </div>
      </form>
      {isLoading ? <FullScreenLoading /> : <></>}
    </>
  );
};

export const BlogsForm = ({
  defaultData,
}: {
  defaultData: IHomepageDetailData["blog"];
}) => {
  const [blogType, setBlogType] = useState("all");

  const [trigger, setTrigger] = useState(false);

  const getCategoryByBlogType = () => {
    switch (blogType) {
      case "all": return "";
      case "promo": return "promotion";
      case "others": return "other";
      default: return blogType;
    }
  }
  const { data: blogsData, isLoading: isLoadingBlogs } = useOne<
    IBlogIdsItem[],
    HttpError
  >({
    resource: `content/dropdown`,
    id: "blogs",
    meta: {
      resource: "data",
      filters: [
        {
          field: "category",
          operator: "eq",
          value: getCategoryByBlogType(),
        }
      ]
    },
    queryOptions: {
      enabled: !!(trigger && blogType),
    } as any,
  });

  return (
    <CollapsibleSection
      label="Blogs (Max 3 blogs per section)"
      defaultOpen={false}
      onClick={() => !trigger && setTrigger(true)}
    >
      <div className="w-full px-4 pt-6 pb-0 sm:px-6">
        <div className="flex flex-wrap gap-x-2 gap-y-2">
          {BLOG_BUTTONS.map((button, index) => (
            <button
              key={button.value}
              onClick={() => setBlogType(button.value)}
              className={`px-3 py-2 ${
                button.value === blogType
                  ? "bg-brand-secondary-500 text-white"
                  : "bg-gray-200 text-black"
              } font-medium text-sm rounded-md`}
            >
              {button.label}
            </button>
          ))}
        </div>
      </div>
      <BlogForm
        key={blogType}
        type={blogType}
        defaultData={get(defaultData, blogType, "")}
        blogList={blogsData?.data ?? []}
        isLoadingBlogs={isLoadingBlogs}
      />
    </CollapsibleSection>
  );
};
